import {
  formatDateOnlyIST,
  formatDateTimeIST,
  formatFileSize,
} from "../utils/hrDateTime";

export const REPEAT_UNIT_OPTIONS = [
  { value: "days", label: "Days" },
  { value: "months", label: "Months" },
  { value: "years", label: "Years" },
];

export const ATTENDANCE_STATUS_OPTIONS = [
  { value: "present", label: "Present" },
  { value: "absent", label: "Absent" },
  { value: "partial", label: "Partial" },
];

export const RESULT_STATUS_OPTIONS = [
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "na", label: "N/A" },
];

export const FITNESS_STATUS_OPTIONS = [
  { value: "Fit", label: "Fit" },
  { value: "Unfit", label: "Unfit" },
  { value: "Fit with restrictions", label: "Fit with restrictions" },
  { value: "Pending Review", label: "Pending Review" },
];

export const SESSION_STATUS_OPTIONS = [
  { value: "planned", label: "Planned" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

export const TRAINING_MODE_OPTIONS = [
  { value: "in_house", label: "In-house" },
  { value: "external", label: "External" },
  { value: "online", label: "Online" },
  { value: "on_the_job", label: "On the Job" },
];

export const HR_ALLOWED_UPLOAD_EXTENSIONS = [
  ".csv",
  ".doc",
  ".docx",
  ".gif",
  ".heic",
  ".jpeg",
  ".jpg",
  ".pdf",
  ".png",
  ".txt",
  ".webp",
  ".xls",
  ".xlsx",
];

export const HR_UPLOAD_ACCEPT = HR_ALLOWED_UPLOAD_EXTENSIONS.join(",");

export const HR_IMAGE_UPLOAD_EXTENSIONS = [
  ".gif",
  ".heic",
  ".jpeg",
  ".jpg",
  ".png",
  ".webp",
];

export const HR_IMAGE_UPLOAD_ACCEPT = HR_IMAGE_UPLOAD_EXTENSIONS.join(",");

export const HR_MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

export const HR_UPLOAD_RULES_MESSAGE =
  `Allowed files: ${HR_ALLOWED_UPLOAD_EXTENSIONS.join(", ")}. Max size: 15 MB.`;

export const truthyLabel = (value) => (value ? "Yes" : "No");

export const getApiErrorMessage = (error, fallback = "Something went wrong") =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

export const getApiErrorMessageAsync = async (
  error,
  fallback = "Something went wrong",
) => {
  const data = error?.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      if (!text) return fallback;

      try {
        const parsed = JSON.parse(text);
        return parsed.message || parsed.error || fallback;
      } catch (_parseError) {
        return text;
      }
    } catch (_blobError) {
      return fallback;
    }
  }

  return getApiErrorMessage(error, fallback);
};

export const getFileExtension = (filename = "") => {
  const dotIndex = filename.lastIndexOf(".");
  return dotIndex >= 0 ? filename.slice(dotIndex).toLowerCase() : "";
};

export const getUploadValidationError = (
  files = [],
  allowedExtensions = HR_ALLOWED_UPLOAD_EXTENSIONS,
) => {
  const incoming = Array.from(files || []);
  const allowed = new Set(allowedExtensions);
  const rulesMessage =
    allowedExtensions === HR_ALLOWED_UPLOAD_EXTENSIONS
      ? HR_UPLOAD_RULES_MESSAGE
      : `Allowed files: ${allowedExtensions.join(", ")}. Max size: 15 MB.`;

  for (const file of incoming) {
    const extension = getFileExtension(file.name);
    if (!allowed.has(extension)) {
      return `'${file.name}': ${extension || "files with no extension"} is not allowed. ${rulesMessage}`;
    }

    if (file.size > HR_MAX_UPLOAD_BYTES) {
      return `'${file.name}' is ${(file.size / (1024 * 1024)).toFixed(1)} MB; the limit is 15 MB.`;
    }

    if (file.size === 0) {
      return `'${file.name}' is empty.`;
    }
  }

  return "";
};

export const downloadBlobResponse = (response, filename) => {
  const blob = new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const openBlobResponse = (response) => {
  const contentType =
    response.headers["content-type"] || "application/octet-stream";
  const fileUrl = window.URL.createObjectURL(
    new Blob([response.data], { type: contentType }),
  );

  window.open(fileUrl, "_blank", "noopener,noreferrer");
  window.setTimeout(() => window.URL.revokeObjectURL(fileUrl), 10000);
};

export const buildEmployeeSelectOptions = (data = []) =>
  data.map((employee) => ({
    value: String(employee.value || employee.id),
    label:
      employee.label ||
      `${employee.person_name || employee.name || "Employee"}${employee.ec_no ? ` (${employee.ec_no})` : ""}`,
    ...employee,
  }));

export const mergeEmployeeSelectOptions = (options = [], employeeRecords = []) => {
  const nextOptions = [...options];
  const selectedValues = new Set(
    nextOptions.map((option) => String(option.value)),
  );

  employeeRecords.forEach((employee) => {
    const value = employee?.value ?? employee?.id ?? employee?.actual_man_power_id;

    if (value === undefined || value === null || value === "") {
      return;
    }

    const stringValue = String(value);
    if (selectedValues.has(stringValue)) {
      return;
    }

    nextOptions.push({
      ...employee,
      value: stringValue,
      label:
        employee.label ||
        `${employee.person_name || employee.employee_name || employee.name || "Employee"}${
          employee.position ? ` (${employee.position})` : ""
        }${employee.ec_no ? ` - ${employee.ec_no}` : ""}`,
    });
    selectedValues.add(stringValue);
  });

  return nextOptions;
};

export const buildUserSelectOptions = (data = []) =>
  data
    .filter((user) => (user.Status || user.status || "active").toLowerCase() === "active")
    .map((user) => ({
      value: String(user.ID ?? user.id),
      label: `${user.Name || user.name || "User"}${
        user.Designation ? ` (${user.Designation})` : ""
      }`,
      ...user,
    }));

export const buildSimpleOptions = (data = [], valueKey, labelKey) =>
  data.map((item) => ({
    value: item[valueKey],
    label: item[labelKey],
    ...item,
  }));

export const buildEmailConfigOptions = (configs = []) =>
  configs.map((config) => ({
    value: String(config.id),
    label: `${config.email || `Sender Email ${config.id}`}${
      config.is_default ? " (default)" : ""
    }`,
  }));

export const buildEmailTemplateOptions = (templates = []) =>
  templates.map((template) => {
    const templateName = String(
      template.template_name || template.temp_name || "",
    ).trim();

    return {
      ...template,
      value: String(template.id || template.value || ""),
      label:
        templateName ||
        template.template_subject ||
        template.subject ||
        `Email Template ${template.id}`,
      template_name: templateName,
      template_to_emails: template.template_to_emails || template.to_emails || "",
      template_subject: template.template_subject || template.subject || "",
      template_body: template.template_body || template.body || "",
    };
  });

export const getSelectedOption = (options, value, fallbackLabel) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const stringValue = String(value);
  return (
    options.find((option) => String(option.value) === stringValue) || {
      value: stringValue,
      label: `${fallbackLabel} ${stringValue}`,
    }
  );
};

export const getDuePillStyle = (status) => {
  switch ((status || "").toLowerCase()) {
    case "active":
      return { background: "#e8f7ef", color: "#157347" };
    case "expiring_soon":
      return { background: "#fff4dd", color: "#9a6700" };
    case "expired":
      return { background: "#fde8e8", color: "#b42318" };
    case "not_trained":
    case "unfit":
      return { background: "#fde8e8", color: "#b42318" };
    default:
      return { background: "#eef2f7", color: "#475467" };
  }
};

export const getFitnessPillStyle = (status) => {
  switch ((status || "").toLowerCase()) {
    case "fit":
      return { background: "#e8f7ef", color: "#157347" };
    case "unfit":
      return { background: "#fde8e8", color: "#b42318" };
    case "fit with restrictions":
      return { background: "#fff4dd", color: "#9a6700" };
    default:
      return { background: "#eef2f7", color: "#475467" };
  }
};

export const renderFileMeta = (file) =>
  [file.file_type?.toUpperCase(), formatFileSize(file.file_size)]
    .filter(Boolean)
    .join(" | ");

export const normaliseReminderDays = (value) => {
  if (Array.isArray(value)) {
    return value.join(",");
  }
  return value || "";
};

export {
  formatDateOnlyIST,
  formatDateTimeIST,
  formatFileSize,
};
